import { useState, useEffect, FormEvent, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { StockItem, ItemType, ItemStatus, StockMovement } from '../../types';
import { updateStockItem, updateStockItemStatus, fetchStockMovements, fetchStockItem, fetchChildItems, CreateStockItemRequest } from '../../services/storage';
import {
  Package, Ruler, Weight, MapPin, DollarSign, Tag, Barcode as BarcodeIcon, Calendar,
  Pencil, ArrowUpDown, History, Save, X, Scissors, Link2, ExternalLink
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { BarcodeDisplay } from './BarcodeDisplay';
import { useToast } from '../../contexts/ToastContext';

const ITEM_TYPES: { value: ItemType; label: string }[] = [
  { value: 'MATERIA_PRIMA', label: 'Matéria-prima' },
  { value: 'FERRAMENTA', label: 'Ferramenta' },
  { value: 'PRODUTO_ACABADO', label: 'Produto Acabado' },
  { value: 'RETALHO', label: 'Retalho' },
  { value: 'EMBALAGEM', label: 'Embalagem' },
];

const STATUS_OPTIONS: { value: ItemStatus; label: string; color: string }[] = [
  { value: 'DISPONIVEL', label: 'Disponível', color: 'bg-green-100 text-green-800' },
  { value: 'RESERVADO', label: 'Reservado', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'CONSUMIDO', label: 'Consumido', color: 'bg-gray-100 text-gray-800' },
  { value: 'DESCARTE', label: 'Descarte', color: 'bg-red-100 text-red-800' },
];

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  'COMPRA': 'Compra',
  'PRODUCAO_ENTRADA': 'Produção (Entrada)',
  'DEVOLUCAO': 'Devolução',
  'CONSUMO': 'Consumo',
  'VENDA': 'Venda',
  'GERACAO_RETALHO': 'Geração de Retalho',
  'DESCARTE': 'Descarte',
  'AJUSTE': 'Ajuste',
};

interface ItemDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessToken: string;
  item: StockItem | null;
  onSuccess: () => void;
  onRecordMovement: (item: StockItem) => void;
  onNavigateToItem?: (item: StockItem) => void;
}

export function ItemDetailDialog({
  open,
  onOpenChange,
  accessToken,
  item,
  onSuccess,
  onRecordMovement,
  onNavigateToItem,
}: ItemDetailDialogProps) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('details');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  // Parent-child relationship state
  const [parentItem, setParentItem] = useState<StockItem | null>(null);
  const [childItems, setChildItems] = useState<StockItem[]>([]);
  const [loadingRelations, setLoadingRelations] = useState(false);

  // Editable fields
  const [name, setName] = useState('');
  const [itemType, setItemType] = useState<ItemType>('MATERIA_PRIMA');
  const [skuCode, setSkuCode] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [thickness, setThickness] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [minStock, setMinStock] = useState('');
  const [unit, setUnit] = useState('');
  const [location, setLocation] = useState('');
  const [costPerUnit, setCostPerUnit] = useState('');
  const [status, setStatus] = useState<ItemStatus>('DISPONIVEL');
  const [tags, setTags] = useState('');

  // Load item data into form
  useEffect(() => {
    if (item) {
      setName(item.name);
      setItemType(item.itemType);
      setSkuCode(item.skuCode || '');
      setWidth(item.width?.toString() || '');
      setHeight(item.height?.toString() || '');
      setThickness(item.thickness?.toString() || '');
      setWeightKg(item.weightKg?.toString() || '');
      setMinStock(item.minStock?.toString() || '');
      setUnit(item.unit || 'un');
      setLocation(item.location || '');
      setCostPerUnit(item.costPerUnit?.toString() || '');
      setStatus(item.status);
      setTags(item.tags?.join(', ') || '');
    }
  }, [item]);

  // Load movements when tab changes
  useEffect(() => {
    if (open && activeTab === 'history' && item) {
      loadMovements();
    }
  }, [open, activeTab, item]);

  // Load parent-child relationships
  const loadRelations = useCallback(async () => {
    if (!item) return;
    setLoadingRelations(true);

    try {
      // If item has a parent, fetch parent item
      if (item.parentItemId) {
        try {
          const parent = await fetchStockItem(accessToken, item.parentItemId);
          setParentItem(parent);
        } catch (e) {
          console.error('Error loading parent item:', e);
          setParentItem(null);
        }
      } else {
        setParentItem(null);
      }

      // If item is MATERIA_PRIMA, fetch children (scraps)
      if (item.itemType === 'MATERIA_PRIMA') {
        try {
          const children = await fetchChildItems(accessToken, item.id);
          setChildItems(children);
        } catch (e) {
          console.error('Error loading child items:', e);
          setChildItems([]);
        }
      } else {
        setChildItems([]);
      }
    } finally {
      setLoadingRelations(false);
    }
  }, [accessToken, item]);

  // Load relations when dialog opens or tab changes to lineage
  useEffect(() => {
    if (open && activeTab === 'lineage' && item) {
      loadRelations();
    }
  }, [open, activeTab, item, loadRelations]);

  const loadMovements = async () => {
    if (!item) return;
    setLoadingMovements(true);
    try {
      const response = await fetchStockMovements(accessToken, { itemId: item.id });
      setMovements(response.items || []);
    } catch (error) {
      console.error('Error loading movements:', error);
    } finally {
      setLoadingMovements(false);
    }
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!item) return;

    setSaving(true);

    try {
      const updateData: Partial<CreateStockItemRequest> = {
        name: name.trim(),
        itemType,
        skuCode: skuCode.trim() || undefined,
        width: width ? parseFloat(width) : undefined,
        height: height ? parseFloat(height) : undefined,
        thickness: thickness ? parseFloat(thickness) : undefined,
        weightKg: weightKg ? parseFloat(weightKg) : undefined,
        minStock: minStock ? parseInt(minStock) : undefined,
        unit: unit.trim() || 'un',
        location: location.trim() || undefined,
        costPerUnit: costPerUnit ? parseFloat(costPerUnit) : undefined,
        tags: tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
      };

      await updateStockItem(accessToken, item.id, updateData);

      // Update status if changed
      if (status !== item.status) {
        await updateStockItemStatus(accessToken, item.id, status, undefined, 'Atualização via painel');
      }

      toast.success('Item atualizado com sucesso!');
      setIsEditing(false);
      onSuccess();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setIsEditing(false);
      setActiveTab('details');
      setParentItem(null);
      setChildItems([]);
    }
    onOpenChange(isOpen);
  };

  // Handle navigation to another item
  const handleNavigateToItem = (targetItem: StockItem) => {
    if (onNavigateToItem) {
      handleClose(false);
      onNavigateToItem(targetItem);
    }
  };

  const handleCancelEdit = () => {
    if (item) {
      setName(item.name);
      setItemType(item.itemType);
      setSkuCode(item.skuCode || '');
      setWidth(item.width?.toString() || '');
      setHeight(item.height?.toString() || '');
      setThickness(item.thickness?.toString() || '');
      setWeightKg(item.weightKg?.toString() || '');
      setMinStock(item.minStock?.toString() || '');
      setUnit(item.unit || 'un');
      setLocation(item.location || '');
      setCostPerUnit(item.costPerUnit?.toString() || '');
      setStatus(item.status);
      setTags(item.tags?.join(', ') || '');
    }
    setIsEditing(false);
  };

  if (!item) return null;

  const statusOption = STATUS_OPTIONS.find(s => s.value === item.status);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] md:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {item.name}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <span className="font-mono text-xs">{item.skuCode}</span>
            <Badge className={cn(statusOption?.color)}>
              {statusOption?.label}
            </Badge>
            {item.belowMinStock && (
              <Badge variant="destructive">Estoque Baixo</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details" className="text-xs md:text-sm">Detalhes</TabsTrigger>
            <TabsTrigger value="lineage" className="text-xs md:text-sm">
              <Link2 className="h-3 w-3 mr-1" />
              Linhagem
            </TabsTrigger>
            <TabsTrigger value="barcodes" className="text-xs md:text-sm">Códigos</TabsTrigger>
            <TabsTrigger value="history" className="text-xs md:text-sm">Histórico</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            <form onSubmit={handleSave}>
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-sm text-muted-foreground">Informações Básicas</h3>
                  {!isEditing && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    {isEditing ? (
                      <Input value={name} onChange={(e) => setName(e.target.value)} required />
                    ) : (
                      <p className="text-sm py-2">{item.name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    {isEditing ? (
                      <Select value={itemType} onValueChange={(v) => setItemType(v as ItemType)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ITEM_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm py-2">{ITEM_TYPES.find(t => t.value === item.itemType)?.label}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    {isEditing ? (
                      <Select value={status} onValueChange={(v) => setStatus(v as ItemStatus)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={cn(statusOption?.color)}>{statusOption?.label}</Badge>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Código SKU</Label>
                    {isEditing ? (
                      <Input value={skuCode} onChange={(e) => setSkuCode(e.target.value)} />
                    ) : (
                      <p className="text-sm py-2 font-mono">{item.skuCode || '-'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Dimensions */}
              <div className="space-y-4 mt-6">
                <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Dimensões
                </h3>
                <div className="grid gap-4 sm:grid-cols-4">
                  {['width', 'height', 'thickness', 'weightKg'].map((field) => {
                    const labels: Record<string, string> = { width: 'Largura (mm)', height: 'Altura (mm)', thickness: 'Espessura (mm)', weightKg: 'Peso (kg)' };
                    const values: Record<string, string> = { width, height, thickness, weightKg };
                    const setters: Record<string, (v: string) => void> = { width: setWidth, height: setHeight, thickness: setThickness, weightKg: setWeightKg };
                    const itemValues: Record<string, number | undefined> = { width: item.width, height: item.height, thickness: item.thickness, weightKg: item.weightKg };

                    return (
                      <div key={field} className="space-y-2">
                        <Label>{labels[field]}</Label>
                        {isEditing ? (
                          <Input type="number" step="0.01" min="0" value={values[field]} onChange={(e) => setters[field](e.target.value)} />
                        ) : (
                          <p className="text-sm py-2">{itemValues[field] ?? '-'}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quantity & Stock */}
              <div className="space-y-4 mt-6">
                <h3 className="font-medium text-sm text-muted-foreground">Estoque</h3>
                <div className="grid gap-4 sm:grid-cols-4">
                  <div className="space-y-2">
                    <Label>Quantidade</Label>
                    <p className="text-2xl font-bold">{item.quantity} <span className="text-sm font-normal text-muted-foreground">{item.unit || 'un'}</span></p>
                  </div>
                  <div className="space-y-2">
                    <Label>Estoque Mínimo</Label>
                    {isEditing ? (
                      <Input type="number" min="0" value={minStock} onChange={(e) => setMinStock(e.target.value)} />
                    ) : (
                      <p className="text-sm py-2">{item.minStock ?? '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Unidade</Label>
                    {isEditing ? (
                      <Input value={unit} onChange={(e) => setUnit(e.target.value)} />
                    ) : (
                      <p className="text-sm py-2">{item.unit || 'un'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Custo Unitário</Label>
                    {isEditing ? (
                      <Input type="number" step="0.01" min="0" value={costPerUnit} onChange={(e) => setCostPerUnit(e.target.value)} />
                    ) : (
                      <p className="text-sm py-2">{item.costPerUnit ? `R$ ${item.costPerUnit.toFixed(2)}` : '-'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4 mt-6">
                <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Localização
                </h3>
                <div className="space-y-2">
                  {isEditing ? (
                    <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex: Prateleira A-01" />
                  ) : (
                    <p className="text-sm py-2">{item.location || '-'}</p>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-4 mt-6">
                <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags
                </h3>
                {isEditing ? (
                  <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Separe por vírgula" />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {item.tags && item.tags.length > 0 ? (
                      item.tags.map((tag, i) => (
                        <Badge key={i} variant="secondary">{tag}</Badge>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">Nenhuma tag</p>
                    )}
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="space-y-4 mt-6">
                <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Datas
                </h3>
                <div className="grid gap-4 sm:grid-cols-3 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Criado em</Label>
                    <p>{new Date(item.createdAt).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Atualizado em</Label>
                    <p>{new Date(item.updatedAt).toLocaleDateString('pt-BR')}</p>
                  </div>
                  {item.purchaseDate && (
                    <div>
                      <Label className="text-muted-foreground">Data de Compra</Label>
                      <p>{new Date(item.purchaseDate).toLocaleDateString('pt-BR')}</p>
                    </div>
                  )}
                </div>
              </div>

              {isEditing && (
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={handleCancelEdit} disabled={saving}>
                    <X className="h-4 w-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving}>
                    <Save className="h-4 w-4 mr-1" />
                    {saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </DialogFooter>
              )}
            </form>

            {!isEditing && (
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => handleClose(false)}>Fechar</Button>
                <Button onClick={() => onRecordMovement(item)}>
                  <ArrowUpDown className="h-4 w-4 mr-1" />
                  Registrar Movimentação
                </Button>
              </DialogFooter>
            )}
          </TabsContent>

          {/* Lineage Tab - Parent/Child relationships */}
          <TabsContent value="lineage" className="mt-4">
            <div className="space-y-6">
              {loadingRelations ? (
                <p className="text-sm text-muted-foreground text-center py-8">Carregando...</p>
              ) : (
                <>
                  {/* Parent Item Section (for RETALHO items) */}
                  {item.itemType === 'RETALHO' && (
                    <div className="space-y-3">
                      <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Material de Origem
                      </h3>
                      {parentItem ? (
                        <div
                          className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => handleNavigateToItem(parentItem)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-mono text-xs text-muted-foreground">{parentItem.skuCode}</div>
                              <div className="font-medium">{parentItem.name}</div>
                              {parentItem.width && parentItem.height && (
                                <div className="text-sm text-muted-foreground mt-1">
                                  {parentItem.width} x {parentItem.height}
                                  {parentItem.thickness && ` x ${parentItem.thickness}`} mm
                                </div>
                              )}
                            </div>
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ) : item.parentItemId ? (
                        <p className="text-sm text-muted-foreground py-4">
                          Material de origem não encontrado (ID: {item.parentItemId})
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground py-4">
                          Este item não possui material de origem vinculado.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Children Items Section (for MATERIA_PRIMA items) */}
                  {item.itemType === 'MATERIA_PRIMA' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                          <Scissors className="h-4 w-4" />
                          Retalhos Gerados
                        </h3>
                      </div>
                      {childItems.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {childItems.map((child) => (
                            <div
                              key={child.id}
                              className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => handleNavigateToItem(child)}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-mono text-xs text-muted-foreground">{child.skuCode}</div>
                                  <div className="text-sm font-medium">{child.name}</div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {child.width} x {child.height}
                                    {child.thickness && ` x ${child.thickness}`} mm
                                    <span className="mx-2">•</span>
                                    Qtd: {child.quantity}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="text-xs">
                                    {child.status === 'DISPONIVEL' ? 'Disponível' : child.status}
                                  </Badge>
                                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                          Nenhum retalho gerado a partir deste material.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Info for other item types */}
                  {item.itemType !== 'MATERIA_PRIMA' && item.itemType !== 'RETALHO' && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      A linhagem é aplicável apenas para Matéria-prima e Retalhos.
                    </p>
                  )}
                </>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => handleClose(false)}>Fechar</Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="barcodes" className="mt-4">
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <BarcodeIcon className="h-4 w-4" />
                Códigos de Barras
              </h3>

              <BarcodeDisplay
                barcodes={item.barcodes || []}
                skuCode={item.skuCode}
                itemName={item.name}
              />
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => handleClose(false)}>Fechar</Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <History className="h-4 w-4" />
                Histórico de Movimentações
              </h3>

              {loadingMovements ? (
                <p className="text-sm text-muted-foreground">Carregando...</p>
              ) : movements.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma movimentação encontrada</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {movements.map((movement) => (
                    <div key={movement.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Badge variant="outline">
                          {MOVEMENT_TYPE_LABELS[movement.movementType] || movement.movementType}
                        </Badge>
                        {movement.reason && (
                          <p className="text-xs text-muted-foreground mt-1">{movement.reason}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "font-medium",
                          movement.quantity > 0 ? "text-green-600" : "text-red-600"
                        )}>
                          {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(movement.createdAt).toLocaleDateString('pt-BR', {
                            day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => handleClose(false)}>Fechar</Button>
              <Button onClick={() => onRecordMovement(item)}>
                <ArrowUpDown className="h-4 w-4 mr-1" />
                Nova Movimentação
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
